import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Attachment, AttachmentEntityType } from '@accounting/shared';
import { api } from '../lib/api';

export const attachmentsKey = (
  companyId: string | null,
  entityType: AttachmentEntityType,
  entityId: string,
) => ['attachments', companyId ?? 'none', entityType, entityId] as const;

export function useAttachments(
  companyId: string | null,
  entityType: AttachmentEntityType,
  entityId: string,
) {
  return useQuery({
    queryKey: attachmentsKey(companyId, entityType, entityId),
    enabled: companyId !== null && !!entityId,
    queryFn: () => {
      const params = new URLSearchParams({ entityType, entityId });
      return api
        .get<{ attachments: Attachment[] }>(`/companies/${companyId}/attachments?${params}`)
        .then((r) => r.attachments);
    },
  });
}

export interface UploadFilePayload {
  fileName: string;
  mimeType: string;
  content: string; // base64, no data: prefix
}

export function useUploadAttachment(
  companyId: string | null,
  entityType: AttachmentEntityType,
  entityId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UploadFilePayload) => {
      if (!companyId) throw new Error('No active company');
      const params = new URLSearchParams({ entityType, entityId });
      return api
        .post<{ attachment: Attachment }>(
          `/companies/${companyId}/attachments?${params}`,
          payload,
        )
        .then((r) => r.attachment);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: attachmentsKey(companyId, entityType, entityId) }),
  });
}

export function useDeleteAttachment(
  companyId: string | null,
  entityType: AttachmentEntityType,
  entityId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api.delete<void>(`/companies/${companyId}/attachments/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: attachmentsKey(companyId, entityType, entityId) }),
  });
}
